import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { projectsAPI, bugsAPI } from '../services/api';
import { format } from 'date-fns';

function ProjectDetails() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [bugs, setBugs] = useState([]);
  const [userBugStats, setUserBugStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const [projectResponse, bugsResponse, userStatsResponse] = await Promise.all([
        projectsAPI.getProject(id),
        bugsAPI.getBugs({ project_id: id }),
        projectsAPI.getUserBugStats(id)
      ]);
      setProject(projectResponse.data);
      setBugs(bugsResponse.data);
      setUserBugStats(userStatsResponse.data);
    } catch (error) {
      toast.error('Failed to load project details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getBugsByStatus = () => {
    const statusCount = {};
    bugs.forEach(bug => {
      statusCount[bug.status] = (statusCount[bug.status] || 0) + 1;
    });
    return statusCount;
  };

  const getBugsByPriority = () => {
    const priorityCount = {};
    bugs.forEach(bug => {
      priorityCount[bug.priority] = (priorityCount[bug.priority] || 0) + 1;
    });
    return priorityCount;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Open': 'danger',
      'In Progress': 'primary',
      'Resolved': 'success',
      'Closed': 'secondary',
      'Re-opened': 'warning',
      'Not a Bug': 'info',
      'Cannot Reproduce': 'secondary',
      'Pending QA': 'warning'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Critical': 'danger',
      'High': 'warning',
      'Medium': 'primary',
      'Low': 'secondary'
    };
    return <Badge bg={priorityColors[priority] || 'secondary'}>{priority}</Badge>;
  };

  if (loading) {
    return (
      <Container fluid style={{ padding: '2rem 3rem' }}>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container fluid style={{ padding: '2rem 3rem' }}>
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <Card.Body style={{ padding: '3rem', textAlign: 'center' }}>
            <h3 style={{ color: '#666' }}>Project not found</h3>
            <Button variant="primary" onClick={() => navigate('/projects')} className="mt-3">
              Back to Projects
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const bugsByStatus = getBugsByStatus();
  const bugsByPriority = getBugsByPriority();

  return (
    <Container fluid style={{ padding: '2rem 3rem' }}>
      {/* Project Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <Badge
            bg="primary"
            style={{ borderRadius: '6px', padding: '8px 14px', fontSize: '16px', fontWeight: '600' }}
          >
            {project.key}
          </Badge>
          <h2 className="fw-bold mb-0" style={{ fontSize: '28px', color: '#1a1a1a' }}>
            {project.name}
          </h2>
        </div>
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/projects')}
          style={{
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Back to Projects
        </Button>
      </div>

      {project.description && (
        <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
          <Card.Body style={{ padding: '1.25rem' }}>
            <p className="mb-0" style={{ fontSize: '14px', color: '#666' }}>{project.description}</p>
          </Card.Body>
        </Card>
      )}

      {/* Stats */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <h5 className="fw-bold mb-3" style={{ fontSize: '18px', color: '#1a1a1a' }}>Bugs by Status</h5>
              <div>
                {Object.keys(bugsByStatus).length > 0 ? (
                  Object.entries(bugsByStatus).map(([status, count]) => (
                    <div key={status} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ fontSize: '14px', color: '#555' }}>{status}</span>
                      <Badge bg="secondary" style={{ borderRadius: '12px', padding: '4px 12px', fontSize: '13px', fontWeight: '500' }}>{count}</Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>No bugs yet</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <h5 className="fw-bold mb-3" style={{ fontSize: '18px', color: '#1a1a1a' }}>Bugs by Priority</h5>
              <div>
                {Object.keys(bugsByPriority).length > 0 ? (
                  Object.entries(bugsByPriority).map(([priority, count]) => (
                    <div key={priority} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ fontSize: '14px', color: '#555' }}>{priority}</span>
                      <Badge bg="secondary" style={{ borderRadius: '12px', padding: '4px 12px', fontSize: '13px', fontWeight: '500' }}>{count}</Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>No bugs yet</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User-wise Bug Status */}
      {userBugStats.length > 0 && (
        <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
          <Card.Body style={{ padding: '1.5rem' }}>
            <h5 className="fw-bold mb-3" style={{ fontSize: '18px', color: '#1a1a1a' }}>User-wise Bug Status</h5>
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <tr>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>User</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Email</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent', textAlign: 'center' }}>Open</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent', textAlign: 'center' }}>In Progress</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent', textAlign: 'center' }}>Resolved</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent', textAlign: 'center' }}>Closed</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent', textAlign: 'center' }}>Re-opened</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent', textAlign: 'center' }}>Other</th>
                    <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent', textAlign: 'center' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {userBugStats.map((userStat) => {
                    const otherCount = (userStat.bugs_by_status['Not a Bug'] || 0) +
                                       (userStat.bugs_by_status['Cannot Reproduce'] || 0) +
                                       (userStat.bugs_by_status['Pending QA'] || 0);
                    return (
                      <tr key={userStat.user_id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#333', fontWeight: '500' }}>{userStat.user_name}</td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{userStat.user_email}</td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666', textAlign: 'center' }}>
                          {userStat.bugs_by_status['Open'] || 0}
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666', textAlign: 'center' }}>
                          {userStat.bugs_by_status['In Progress'] || 0}
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666', textAlign: 'center' }}>
                          {userStat.bugs_by_status['Resolved'] || 0}
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666', textAlign: 'center' }}>
                          {userStat.bugs_by_status['Closed'] || 0}
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666', textAlign: 'center' }}>
                          {userStat.bugs_by_status['Re-opened'] || 0}
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666', textAlign: 'center' }}>
                          {otherCount}
                        </td>
                        <td style={{ fontSize: '14px', padding: '12px 8px', color: '#333', fontWeight: '600', textAlign: 'center' }}>
                          {userStat.total_assigned}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Bugs Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
        <Card.Body style={{ padding: '1.5rem' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0" style={{ fontSize: '18px', color: '#1a1a1a' }}>All Bugs ({bugs.length})</h5>
            <Button
              variant="primary"
              onClick={() => navigate('/bugs/new')}
              style={{
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '600',
                fontSize: '13px',
                border: 'none'
              }}
            >
              + Create Bug
            </Button>
          </div>
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead style={{ borderBottom: '2px solid #e0e0e0' }}>
                <tr>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Bug ID</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Title</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Priority</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Status</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Assignee</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Reporter</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {bugs.length > 0 ? (
                  bugs.map((bug) => (
                    <tr
                      key={bug.id}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid #f5f5f5'
                      }}
                      onClick={() => navigate(`/bugs/${bug.id}`)}
                    >
                      <td style={{ fontSize: '14px', padding: '12px 8px' }}>
                        <strong style={{ color: '#0066cc' }}>{bug.bug_number}</strong>
                      </td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#333' }}>{bug.title}</td>
                      <td style={{ padding: '12px 8px' }}>{getPriorityBadge(bug.priority)}</td>
                      <td style={{ padding: '12px 8px' }}>{getStatusBadge(bug.status)}</td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{bug.assignee_name || '-'}</td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{bug.reporter_name}</td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#999' }}>{format(new Date(bug.created_at), 'MMM dd, yyyy')}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted" style={{ padding: '24px' }}>
                      No bugs found in this project
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ProjectDetails;
